import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, RotateCcw, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HandoffReasonBadge, REASON_OPTIONS, fmtDateTime } from '@/components/notifications/handoffUtils';

const DETAIL_LABELS = { model: 'Model', expected_price: 'Expected price', callback_time: 'Callback time', appointment: 'Appointment' };

const Select = ({ value, onChange, children, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
    className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm">{children}</select>
);

const HandoffRow = ({ h, onChanged }) => {
  const navigate = useNavigate();
  const act = async (path, msg) => {
    try { await api.post(`/handoffs/${h.id}/${path}`, {}); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div data-testid={`handoff-row-${h.id}`} className={`bg-white border rounded-lg p-4 ${h.status === 'open' ? 'border-stone-300' : 'border-stone-200 opacity-80'}`}>
      <div className="flex flex-wrap items-center gap-2">
        {!h.read && <span className="w-2 h-2 rounded-full bg-rose-700" />}
        <span className="font-semibold">{h.patient_name || 'Patient'}</span>
        <span className="text-xs text-stone-500">{h.patient_mobile}</span>
        <HandoffReasonBadge reason={h.reason} label={h.reason_label} />
        {h.reminders > 0 && h.status === 'open' && <span className="text-[11px] font-semibold text-amber-700 uppercase">Overdue · {h.reminders} reminder{h.reminders > 1 ? 's' : ''}</span>}
        <span className={`ml-auto text-[11px] font-semibold uppercase ${h.status === 'open' ? 'text-rose-800' : 'text-emerald-700'}`} data-testid={`handoff-status-${h.id}`}>{h.status}</span>
      </div>
      {h.note && <p className="mt-2 text-sm text-stone-800">{h.note}</p>}
      {Object.keys(h.details).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600">
          {Object.entries(h.details).map(([k, v]) => <span key={k}><b>{DETAIL_LABELS[k] || k}:</b> {String(v)}</span>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
        <span>{fmtDateTime(h.created_at)}</span>
        <span>From: {h.actor}</span>
        <span>To: {h.owner_name || 'Clinic head / admins'}</span>
        {h.clinic_name && <span>{h.clinic_name}</span>}
        {h.done_by && <span>Done by {h.done_by} · {fmtDateTime(h.done_at)}</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" data-testid={`handoff-open-patient-${h.id}`}
          onClick={() => { api.post(`/handoffs/${h.id}/read`).catch(() => {}); navigate(`/tickets?ticket=${h.ticket_id}`); }}>
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open patient
        </Button>
        {h.status === 'open'
          ? <Button size="sm" data-testid={`handoff-done-${h.id}`} onClick={() => act('done', 'Marked done')} className="bg-stone-900 hover:bg-stone-800"><Check className="w-3.5 h-3.5 mr-1" /> Mark done</Button>
          : <Button size="sm" variant="ghost" data-testid={`handoff-reopen-${h.id}`} onClick={() => act('reopen', 'Reopened')}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen</Button>}
      </div>
    </div>
  );
};

export default function HandoffsPage() {
  const [f, setF] = useState({ status: '', reason: '', from: '', to: '', q: '', all: false });
  const [res, setRes] = useState({ total: 0, data: [], is_admin: false });
  const [loading, setLoading] = useState(true);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v !== false));
    try { const { data } = await api.get('/handoffs', { params: { ...params, limit: 200 } }); setRes(data); }
    catch { toast.error('Could not load hand-offs'); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => { clearTimeout(t); };
  }, [load]);

  return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="handoffs-title">Hand-offs</h1>
          <p className="text-sm text-stone-600 mt-1">Patients Priya or the AI agent handed to you — kept as full history.</p>
          <div className="mt-5 flex flex-wrap gap-2 items-center" data-testid="handoffs-filters">
            <Input data-testid="handoffs-search" placeholder="Search patient, mobile, note" value={f.q} onChange={(e) => set('q')(e.target.value)} className="h-9 w-56" />
            <Select testId="handoffs-status-filter" value={f.status} onChange={set('status')}>
              <option value="">All statuses</option><option value="open">Open</option><option value="done">Done</option>
            </Select>
            <Select testId="handoffs-reason-filter" value={f.reason} onChange={set('reason')}>
              <option value="">All reasons</option>
              {REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Input type="date" data-testid="handoffs-from" value={f.from} onChange={(e) => set('from')(e.target.value)} className="h-9 w-40" />
            <Input type="date" data-testid="handoffs-to" value={f.to} onChange={(e) => set('to')(e.target.value)} className="h-9 w-40" />
            {res.is_admin && (
              <label className="flex items-center gap-1.5 text-sm text-stone-700">
                <input type="checkbox" data-testid="handoffs-all-toggle" checked={f.all} onChange={(e) => set('all')(e.target.checked)} /> Everyone's hand-offs
              </label>
            )}
          </div>
          <div className="mt-3 text-xs text-stone-500" data-testid="handoffs-total">{loading ? 'Loading…' : `${res.total} hand-off${res.total === 1 ? '' : 's'}`}</div>
          <div className="mt-3 space-y-3">
            {!loading && res.data.length === 0 && <div className="text-sm text-stone-500 bg-white border border-dashed rounded-lg p-8 text-center" data-testid="handoffs-empty">No hand-offs match these filters.</div>}
            {res.data.map((h) => <HandoffRow key={h.id} h={h} onChanged={load} />)}
          </div>
        </div>
      </div>
  );
}
